import axios from 'axios';
import { Component, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Input } from '@/components/core/Input';
import { Textarea } from '@/components/core/Textarea';
import { AttributeComponent } from '@/components/datamodel/AttributeComponent';
import { LayoutProvider } from '@/components/datamodel/hooks/useLayout';
import { FocusAttributeProvider } from '@/dialogs/item-editor/Context/FocusAttributeProvider';
import { FormResetProvider } from '@/dialogs/item-editor/Context/FormResetContext';
import { useDataModel } from '@/hooks/useDataModel';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { DataModel, DataModelAttribute } from '@/types/DataModel';

import { BusinessPartner, contactErrorMessage, formatContactDate, isContactEmailValid } from './businessPartner';
import { useCreateContact, useUpdateContact } from './businessPartner.queries';
import { ContactImageField, type ContactImageFieldHandle } from './ContactImageField';

interface ContactEditorDialogProps {
    open: boolean;
    contact?: BusinessPartner;
    onClose: () => void;
}

const readContactAttributes = (dataModel: DataModel | undefined): DataModelAttribute[] => {
    const model = dataModel as (DataModel & { getContactAttributes?: () => DataModelAttribute[] }) | undefined;
    return model?.getContactAttributes?.() ?? [];
};

const hasEditLayout = (dataModel: DataModel | undefined): boolean => {
    try {
        return Boolean(dataModel?.hasLayout('edit'));
    } catch {
        return false;
    }
};

class ContactAttributeBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
    state = { failed: false };

    static getDerivedStateFromError(): { failed: boolean } {
        return { failed: true };
    }

    render() {
        return this.state.failed ? null : this.props.children;
    }
}

const ContactEditorDialog = ({ open, contact, onClose }: ContactEditorDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { dataModel } = useDataModel();
    const editing = contact?.id != null;
    const readOnly = editing && contact?.organizationId != null;
    const attributes = useMemo(() => readContactAttributes(dataModel), [dataModel]);
    const editLayoutAvailable = useMemo(() => hasEditLayout(dataModel), [dataModel]);
    const form = useForm();
    const { reset, getValues } = form;
    const [name, setName] = useState(contact?.name ?? '');
    const [email, setEmail] = useState(contact?.email ?? '');
    const [address, setAddress] = useState(contact?.address ?? '');
    const [url, setUrl] = useState(contact?.url ?? '');
    const [imageUrl, setImageUrl] = useState(contact?.imageUrl);
    const imageFieldRef = useRef<ContactImageFieldHandle>(null);
    const createContact = useCreateContact();
    const updateContact = useUpdateContact();
    const saving = createContact.isPending || updateContact.isPending;
    const emailValid = isContactEmailValid(email);
    const canSave = !saving && (readOnly || (name.trim().length > 0 && emailValid));

    useEffect(() => {
        if (!open) {
            return;
        }
        setName(contact?.name ?? '');
        setEmail(contact?.email ?? '');
        setAddress(contact?.address ?? '');
        setUrl(contact?.url ?? '');
        setImageUrl(contact?.imageUrl);
        reset(contact?.customValues ?? {});
    }, [open, contact, reset]);

    useEffect(() => {
        if (attributes.length === 0) {
            return;
        }
        try {
            getRootScope(getInjector()).prepareAttributes?.(attributes);
        } catch {
            // Attribute editors still render without the Angular prepare step.
        }
    }, [attributes]);

    const save = async () => {
        if (!canSave) {
            return;
        }
        let nextImageUrl = imageUrl;
        if (!readOnly) {
            try {
                nextImageUrl = await imageFieldRef.current?.uploadPending();
            } catch {
                return;
            }
        }
        const customValues: Record<string, unknown> = { ...(contact?.customValues ?? {}) };
        const values = getValues();
        attributes.forEach((attribute) => {
            if (values[attribute.name] !== undefined) {
                customValues[attribute.name] = values[attribute.name];
            }
        });
        const payload: BusinessPartner = {
            ...(contact ?? {}),
            name: readOnly ? contact?.name : name.trim(),
            email: readOnly ? contact?.email : email,
            address: readOnly ? contact?.address : address,
            url: readOnly ? contact?.url : url,
            imageUrl: readOnly ? contact?.imageUrl : nextImageUrl,
            customValues,
        };
        delete payload.managedContact;
        const onSuccess = () => {
            toast.success(editing ? 'CONTACT_SUCCESSFULLY_UPDATED' : 'CONTACT_SUCCESSFULLY_ADDED');
            onClose();
        };
        const onError = (error: unknown) => {
            const data = axios.isAxiosError(error) ? error.response?.data : undefined;
            toast.error(contactErrorMessage(data, t, 'SAVE_ERROR_MESSAGE') ?? t('SAVE_ERROR_MESSAGE'));
        };
        if (editing) {
            updateContact.mutate(payload, { onSuccess, onError });
        } else {
            createContact.mutate(payload, { onSuccess, onError });
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent
                variant="large"
                onPointerDownOutside={(event) => {
                    if (event.target instanceof Element && event.target.closest('.select-content')) {
                        event.preventDefault();
                    }
                }}
                onInteractOutside={(event) => {
                    if (event.target instanceof Element && event.target.closest('.select-content')) {
                        event.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogHeaderDefault>{t('CONTACT_MANAGEMENT.EDIT')}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody className="tw-max-h-[70vh] tw-overflow-y-auto">
                    <ContactImageField
                        ref={imageFieldRef}
                        imageUrl={imageUrl}
                        readOnly={Boolean(readOnly)}
                        onChange={setImageUrl}
                    />
                    {readOnly && (
                        <div className="tw-mt-4 tw-text-center">
                            <p className="tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">{contact?.name}</p>
                            {contact?.email && (
                                <a
                                    className="tw-text-t-sm tw-text-t-secondary tw-transition-colors tw-duration-100 tw-ease-in-out hover:tw-text-t-primary lg:tw-text-t-base"
                                    href={`mailto:${contact.email}`}
                                >
                                    {contact.email}
                                </a>
                            )}
                        </div>
                    )}
                    <FormProvider {...form}>
                        <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-4">
                            <label className="tw-flex tw-flex-col tw-gap-1" htmlFor="contact-name">
                                <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('Name')}</span>
                                {readOnly ? (
                                    <p className="tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">{contact?.name}</p>
                                ) : (
                                    <Input
                                        id="contact-name"
                                        value={name}
                                        required
                                        onChange={(event) => setName(event.target.value)}
                                    />
                                )}
                            </label>
                            {!readOnly && (
                                <label className="tw-flex tw-flex-col tw-gap-1" htmlFor="contact-email">
                                    <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('Email')}</span>
                                    <Input
                                        id="contact-email"
                                        type="email"
                                        value={email}
                                        aria-invalid={!emailValid}
                                        onChange={(event) => setEmail(event.target.value)}
                                    />
                                </label>
                            )}
                            {readOnly && (
                                <>
                                    <div className="tw-flex tw-flex-col tw-gap-1">
                                        <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                            {t('CONTACT_MANAGEMENT.CONTACT_ROLE')}
                                        </span>
                                        <p className="tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                            {contact?.contactRole ? t(contact.contactRole) : ''}
                                        </p>
                                    </div>
                                    <div className="tw-flex tw-flex-col tw-gap-1">
                                        <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                            {t('CONTACT_MANAGEMENT.DATE')}
                                        </span>
                                        <p className="tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                            {formatContactDate(contact?.date, false)}
                                        </p>
                                    </div>
                                </>
                            )}
                            <label className="tw-flex tw-flex-col tw-gap-1" htmlFor="contact-address">
                                <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                    {t('CONTACT_MANAGEMENT.ADDRESS')}
                                </span>
                                {readOnly ? (
                                    <p className="tw-whitespace-pre-wrap tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                        {contact?.address}
                                    </p>
                                ) : (
                                    <Textarea
                                        id="contact-address"
                                        rows={10}
                                        placeholder={t('ADDRESS')}
                                        value={address}
                                        onChange={(event) => setAddress(event.target.value)}
                                    />
                                )}
                            </label>
                            <label className="tw-flex tw-flex-col tw-gap-1" htmlFor="contact-url">
                                <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                    {t('CONTACT_MANAGEMENT.URL')}
                                </span>
                                {readOnly ? (
                                    <p className="tw-break-all tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">{contact?.url}</p>
                                ) : (
                                    <Input
                                        id="contact-url"
                                        value={url}
                                        onChange={(event) => setUrl(event.target.value)}
                                    />
                                )}
                            </label>
                            {attributes.length > 0 && editLayoutAvailable && (
                                <ContactAttributeBoundary>
                                    <FocusAttributeProvider>
                                        <FormResetProvider>
                                            <LayoutProvider layoutName="edit">
                                                {attributes.map((attribute) => (
                                                    <AttributeComponent
                                                        key={attribute.name}
                                                        attribute={attribute}
                                                        disableUrlSync
                                                        direction="vertical"
                                                    />
                                                ))}
                                            </LayoutProvider>
                                        </FormResetProvider>
                                    </FocusAttributeProvider>
                                </ContactAttributeBoundary>
                            )}
                        </div>
                    </FormProvider>
                </DialogBody>
                <DialogFooter>
                    <Button type="button" onClick={save} disabled={!canSave} loading={saving}>
                        {t('SAVE')}
                    </Button>
                    <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
                        {t('CANCEL')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { ContactEditorDialog };
